import { Component, DoCheck, EventEmitter, HostListener, Input, Output, ChangeDetectionStrategy } from '@angular/core'
import { ApiService } from 'src/app/services/api.service'
import { SyncPhaseDto } from 'src/app/services/dtos'
import { UiService } from 'src/app/services/ui.service'
import { timeAgoString } from 'src/app/shared/utils'
import { AccountService } from '../../services/account.service'
import { Application, ApplicationProviderService } from '../../services/application.service'
import { LaunchRestoreSettings, LaunchSyncDialogBase } from '../launch-sync-dialog-base'

import { AccessLevelService, AppPermission } from 'src/app/services/access-level.service'

export interface ConcludeRecoverySettings {
  restoreSettings: Array<LaunchRestoreSettings>
}

interface StatusInfo {
  label: string
  inProgress: boolean
}

interface SelectionView {
  toCancel: Application[]
  toConclude: Application[]
  allCancel: boolean
  hasMixed: boolean
  cancelledAppNames: string
  concludedAppNames: string
}

const EMPTY_STATUS_INFO: StatusInfo = { label: '', inProgress: false }

@Component({
  selector: 'conclude-recovery-dialog',
  templateUrl: './conclude-recovery-dialog.component.html',
  styleUrls: ['./conclude-recovery-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ConcludeRecoveryDialogComponent extends LaunchSyncDialogBase implements DoCheck {
  @Input() currentlySyncing: boolean = false
  @Output() conclude = new EventEmitter<ConcludeRecoverySettings>()

  private recoveredApplicationIds = new Set<string>()

  // Mirror of the conclude-test rework: route apps whose initial
  // FAILOVER restore hasn't completed through cancel semantics (cancel note,
  // CANCEL RECOVERY label) while still letting completed apps flow through
  // the normal conclude path in the same dialog.
  private selectionView: SelectionView = {
    toCancel: [],
    toConclude: [],
    allCancel: false,
    hasMixed: false,
    cancelledAppNames: '',
    concludedAppNames: '',
  }

  // Per-app status info computed once per CD tick (in ngDoCheck) and looked up
  // by the template — prevents the per-row template from re-running
  // `applicationStatusInfo()` multiple times per row per CD.
  private statusInfoCache = new Map<Application, StatusInfo>()

  // "Show only completed" filter — surfaced when at least one
  // candidate app is mid-restore so the user can focus on conclude-bound
  // rows. Toggles the in-progress filter in `applicationIsCandidate`.
  showOnlyCompleted: boolean = false

  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKeydown(event: KeyboardEvent): void {
    if (!this.isTopmostModal()) return
    if (this.launching) return
    event.preventDefault()
    this.onCancelClick()
  }

  constructor(
    applicationProviderService: ApplicationProviderService,
    accountService: AccountService,
    apiService: ApiService,
    uiService: UiService,
    accessLevelService: AccessLevelService
  ) {
    super(applicationProviderService, accountService, apiService, uiService, accessLevelService)
  }

  ngDoCheck(): void {
    // Holding both caches holds every cancel-vs-conclude binding: title, disclaimers,
    // submit-button variant, and the per-row status pills.
    if (this.frozenForSubmit) return
    this.rebuildStatusInfoCache()
    this.selectionView = this.computeSelectionView()
  }

  private rebuildStatusInfoCache(): void {
    this.statusInfoCache.clear()
    const apps = this._applications ?? []
    for (const app of apps) {
      this.statusInfoCache.set(app, this.applicationStatusInfo(app))
    }
  }

  statusInfoFor(app: Application): StatusInfo {
    return this.statusInfoCache.get(app) ?? EMPTY_STATUS_INFO
  }

  private computeSelectionView(): SelectionView {
    const selected = this.selectedApplications
    const toCancel = selected.filter((app) => this.statusInfoFor(app).inProgress)
    const toConclude = selected.filter((app) => !this.statusInfoFor(app).inProgress)
    return {
      toCancel,
      toConclude,
      allCancel: selected.length > 0 && toConclude.length === 0,
      hasMixed: toCancel.length > 0 && toConclude.length > 0,
      cancelledAppNames: toCancel.map((app) => app.name).join(', '),
      concludedAppNames: toConclude.map((app) => app.name).join(', '),
    }
  }

  // Same in-progress signal pattern as conclude-test-dialog. The displayed
  // pill and the cancel/conclude routing read from a single cached source so
  // they can't disagree.
  private applicationStatusInfo(app: Application): StatusInfo {
    if (app.currentlyRestoring) {
      return { label: 'Restore in progress…', inProgress: true }
    }
    if (app.lastRestoreComplete) {
      const verb =
        app.lastRestoreSuccess && app.lastRestoreSuccess >= (app.lastRestoreFailure ?? new Date(0))
          ? 'Restored'
          : 'Restore failed'
      return { label: `${verb} ${timeAgoString(app.lastRestoreComplete)}`, inProgress: false }
    }
    return { label: 'Awaiting restore…', inProgress: true }
  }

  get selectedToCancel(): Application[] {
    return this.selectionView.toCancel
  }

  get selectedToConclude(): Application[] {
    return this.selectionView.toConclude
  }

  get allSelectedWillBeCancelled(): boolean {
    return this.selectionView.allCancel
  }

  get hasMixedSelection(): boolean {
    return this.selectionView.hasMixed
  }

  get dialogTitle(): string {
    return this.selectionView.allCancel
      ? 'Cancel recovery for selected applications'
      : 'Conclude recovery for selected applications'
  }

  get cancelledAppNames(): string {
    return this.selectionView.cancelledAppNames
  }

  get concludedAppNames(): string {
    return this.selectionView.concludedAppNames
  }

  get canSubmit(): boolean {
    return this.selectedApplications.length > 0
  }

  onConcludeClick() {
    // Concluding starts its own restore back to standby, and that latch lands while this
    // dialog is still open. Freeze first so a committed submit can't rewrite itself into the
    // cancel variant mid-request.
    this.freezeForSubmit()
    this.conclude.emit({
      restoreSettings: this.selectedApplications.map((app) => {
        const setting: LaunchRestoreSettings = { application: app }
        // Same per-row cancel tag as conclude-test so a mixed batch records
        // distinct semantics per app downstream. The dialog flips its own
        // submit-button label off the same per-row signal.
        if (this.statusInfoFor(app).inProgress) {
          setting.cancelledMidRestore = true
        }
        return setting
      }),
    })
  }

  protected applicationIsCandidate(application: Application) {
    // After concluding the recovery, the application syncPhase reverts to STANDBY before
    // the dialog is closed.  That causes some jumping around on the screen.
    // To fix this, we'll cache the IDs of applications that were ever in
    // FAILOVER within this dialog, and include them in the candidate
    // applications.
    const inRecovery = application.syncPhase === SyncPhaseDto.FAILOVER
    if (inRecovery) {
      this.recoveredApplicationIds.add(application.applicationId)
    }
    const isCandidate = inRecovery || this.recoveredApplicationIds.has(application.applicationId)
    if (!isCandidate) return false
    // Azure can't cancel an in-flight restore (backend limitation, matches the
    // page-level `isAzureRestoreInFlight` gate in application.component). Hide
    // the row entirely so the user isn't offered a "cancel" they can't perform.
    // Remove this filter once Azure mid-restore cancellation is supported.
    if (application.isAzure && application.currentlyRestoring) return false
    if (this.showOnlyCompleted && this.statusInfoFor(application).inProgress) {
      return false
    }
    return true
  }

  get hasAnyInProgressCandidate(): boolean {
    for (const app of this._applications ?? []) {
      const inRecovery = app.syncPhase === SyncPhaseDto.FAILOVER
      const isCandidate = inRecovery || this.recoveredApplicationIds.has(app.applicationId)
      if (isCandidate && this.statusInfoFor(app).inProgress) {
        return true
      }
    }
    return false
  }

  protected getRequiredPermission(): AppPermission {
    return AppPermission.MANAGE_RECOVERY
  }
}
